import { useAPI } from "@/contexts/APIContext";
import { useNotification } from "@/contexts/NotificationContext";
import { cn } from "@/utils/cn";
import { ReactNode, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";

interface HeaderProps {
  children?: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  isExternal?: boolean;
  showBadge?: boolean;
}

function DefaultNavigation() {
  const { staticData, user } = useAPI();
  const { unreadCount } = useNotification();
  const isAuthenticated = !!user;
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const authenticatedLinks: NavItem[] = [
    { to: "/dashboard", label: "Find Partners" },
    { to: "/appointments", label: "Upcoming Chats" },
    ...(staticData?.adminUserId ? [{ to: `/chat/${staticData.adminUserId}`, label: "Chat with Admin" }] : []),
    { to: "/chats", label: "Messages", showBadge: true },
    { to: "/profile", label: "Profile" },
  ];

  const unauthenticatedLinks: NavItem[] = [
    { to: "/features", label: "Features", isExternal: true },
    { to: "/login", label: "Sign Up" },
  ];

  const mobileAuthenticatedLinks: NavItem[] = [...authenticatedLinks, { to: "/settings", label: "Settings" }];

  // Prevent scrolling when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  return (
    <>
      {/* Burger menu button - visible on mobile only */}
      <button className="md:hidden focus:outline-none z-50" onClick={toggleMenu} aria-label="Toggle navigation menu">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6 text-gray-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke={isMenuOpen ? "white" : "currentColor"}
        >
          {isMenuOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Desktop navigation */}
      <nav className="hidden md:flex items-center space-x-6 whitespace-nowrap">
        {(isAuthenticated ? authenticatedLinks : unauthenticatedLinks).map((link) =>
          link.isExternal ? (
            <a key={link.to} href={link.to} className="text-gray-600 hover:text-primary font-medium">
              {link.label}
            </a>
          ) : (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                isActive ? "text-primary font-medium" : "text-gray-600 hover:text-primary font-medium"
              }
            >
              <div className="flex items-center">
                {link.label}
                {link.showBadge && unreadCount > 0 && (
                  <span className="ml-1.5 bg-red-500 text-white text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </div>
            </NavLink>
          )
        )}
      </nav>

      {/* Full-screen Mobile navigation */}
      {isMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-primary flex flex-col z-40">
          <div className="flex-1 flex items-center justify-center">
            <nav className="flex flex-col items-center w-full py-10">
              {(isAuthenticated ? mobileAuthenticatedLinks : unauthenticatedLinks).map((link) =>
                link.isExternal ? (
                  <a
                    key={link.to}
                    href={link.to}
                    className="py-5 text-xl font-medium text-white opacity-90 hover:opacity-100 text-center w-full"
                    onClick={toggleMenu}
                  >
                    {link.label}
                  </a>
                ) : (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    className={({ isActive }) =>
                      `py-5 text-xl font-medium text-center w-full flex items-center justify-center ${
                        isActive ? "text-white font-bold" : "text-white opacity-90 hover:opacity-100"
                      }`
                    }
                    onClick={toggleMenu}
                  >
                    <span>{link.label}</span>
                    {link.showBadge && unreadCount > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs font-semibold rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </NavLink>
                )
              )}
            </nav>
          </div>
        </div>
      )}
    </>
  );
}

export default function Header({ children }: HeaderProps) {
  const { user } = useAPI();
  const isAuthenticated = !!user;

  return (
    <header className="relative flex items-center justify-between px-6 py-4 md:px-12 z-50">
      <Link to={isAuthenticated ? "/dashboard" : "/"} className={cn("text-2xl font-bold text-primary")}>
        pimaru
      </Link>

      {children || <DefaultNavigation />}
    </header>
  );
}
